// Global Instructions Rule Applied! - March 31, 2026
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useDrag, useDrop } from 'react-dnd'
import { Drawer, Divider, Button as AntButton, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faArrowRotateLeft,
  faCheck,
  faGripVertical,
  faPlus,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons'
import { portalGradients } from '../../../portal-configs/colors.js'
import { formatKeyToLabel } from '../../utils/utilities.ts'
import Button from '../Button.jsx'
import { AddColumnsPanel, type AddColumnsCatalog } from './AddColumnsPanel.tsx'
import { FilterPanel, createFilterDraftRow, type FilterDraftRow } from './FilterPanel.tsx'
import { SortPanel, createSortDraftRow, type SortDraftRow } from './SortPanel.tsx'
import type { FilterOptions } from '../TableView/TableViewTypes.ts'
import type { ViewConfigColumn } from './types.ts'
import type { FilterValue, SortConfig } from '../../../state_manager/interfaces/deals.ts'
import {
  isContactFilterColumn,
  isCustomFilterColumn,
  isDateRangeFilterColumn,
  isRangeFilterColumn,
  isSelectFilterColumn
} from './filterColumnKind.ts'

export type ViewConfigDrawerMode = 'columns' | 'filters' | 'sorting'

/** Persist column order / visibility to `user_preferences_reports.data.table_column_keys` */
export interface ColumnLayoutPersistenceProps {
  /** The drawer awaits these but never reads what they resolve to; callers surface their own persist result. */
  onPersistFieldOrder: (fieldNames: string[]) => Promise<unknown> | void
  onResetToDefaults: () => Promise<unknown> | void
  resolveColumn: (valueKey: string) => ViewConfigColumn | undefined
  /** Append order for keys newly checked in Add columns (Deal catalog, then Related) */
  catalogFieldNameOrder: string[]
  /**
   * Field names in per-report `cfg.reporting_columns` `display_order` for this report. When the last-saved layout
   * (`baseline`) matches this list, Reset to default is disabled — already using DB defaults.
   */
  defaultTableColumnKeys?: string[] | null
}

export interface SortingPersistenceProps {
  currentSorting: SortConfig[]
  defaultSorting?: SortConfig[]
  onApplySorting: (sorting: SortConfig[]) => Promise<void> | void
  onResetToDefaults: () => Promise<void> | void
}

export interface FilteringPersistenceProps {
  currentFilters: FilterValue[]
  defaultFilters?: FilterValue[]
  onApplyFilters: (filters: FilterValue[]) => Promise<void> | void
  onResetToDefaults: () => Promise<void> | void
}

const COLUMN_ROW_TYPE = 'view_config_column'

function stableSerialize(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableSerialize(v)}`)
    return `{${entries.join(',')}}`
  }
  return JSON.stringify(value)
}

interface EditColumnsDrawerProps {
  open: boolean
  /** Which tool is shown: column editor, filters, or sorting */
  mode?: ViewConfigDrawerMode
  moduleTitle: string
  columns: ViewConfigColumn[]
  onClose: () => void
  /** Same shape as TableView — keys are `filterKey || valueKey` per column */
  filterOptions?: FilterOptions
  filterOptionsLoading?: boolean
  /**
   * Optional Deal / Related catalogs for Add columns (from `reporting_modules` + `reporting_columns`).
   * When omitted, Add columns uses legacy split on `columns` only.
   */
  addColumnsCatalog?: AddColumnsCatalog
  /** Labels for Add columns tabs (default Deal / Related). E.g. Company / Related for Companies. */
  addColumnsTabLabels?: { primary: string; related: string }
  /** When set (e.g. Deals), reorder/remove/add persist to user preferences */
  columnLayout?: ColumnLayoutPersistenceProps
  /** Optional controlled filtering contract for Filter panel */
  filteringConfig?: FilteringPersistenceProps
  /** Optional controlled sorting contract for Sort panel */
  sortingConfig?: SortingPersistenceProps
  /**
   * Optional "options" section rendered above the FilterPanel inside the
   * filters tab. Used by reports that surface RPC-level boolean / date
   * toggles (include warehoused, include converted out, as-of date, etc.) —
   * i.e. parameters that act like filters but are passed as RPC arguments.
   *
   * Behaves like the Filter section: changes go into a draft, the drawer's
   * existing Cancel / Apply / Reset buttons commit / revert / reset both
   * filters AND options together. The owner of this prop is responsible for
   * holding the draft + applied state and exposing the contract below.
   */
  filterOptionsConfig?: FilterOptionsPersistenceProps
}

export interface FilterOptionsPersistenceProps {
  /** The panel content (controls bound to draft state). */
  panel: React.ReactNode
  /** True when the user has edits not yet applied. */
  isDirty: boolean
  /** True when the *applied* state matches defaults (disables Reset). */
  isAlreadyDefault: boolean
  onApply: () => Promise<void> | void
  onCancel: () => void
  onResetToDefaults: () => Promise<void> | void
}


interface DraggableColumnRowProps {
  col: ViewConfigColumn
  index: number
  moveColumn: (dragIndex: number, hoverIndex: number) => void
  onRemove: (valueKey: string) => void
}

function DraggableColumnRow({ col, index, moveColumn, onRemove }: DraggableColumnRowProps) {
  const rowRef = useRef<HTMLDivElement>(null)
  const gripRef = useRef<HTMLSpanElement>(null)

  const [{ isDragging }, drag] = useDrag({
    type: COLUMN_ROW_TYPE,
    item: { index, valueKey: col.valueKey },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [, drop] = useDrop({
    accept: COLUMN_ROW_TYPE,
    hover: (draggedItem: { index: number; valueKey: string }, monitor) => {
      if (!rowRef.current) return
      const dragIndex = draggedItem.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = rowRef.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveColumn(dragIndex, hoverIndex)
      draggedItem.index = hoverIndex
    }
  })

  drag(gripRef)
  drop(rowRef)

  return (
    <div
      ref={rowRef}
      className={`group flex min-w-0 items-center gap-2 rounded-md bg-gray-100 px-2 py-2.5 pl-2 text-gray-900 transition-opacity ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <span
        ref={gripRef}
        className='flex h-8 w-6 flex-shrink-0 cursor-grab items-center justify-center text-gray-400 active:cursor-grabbing'
      >
        <FontAwesomeIcon icon={faGripVertical} className='text-sm' />
      </span>
      <span className='min-w-0 flex-1 truncate text-sm font-medium leading-tight'>
        {col.columnTitle}
        {col.moduleKey ? (
          <span className='ml-1 font-normal text-gray-500'>({formatKeyToLabel(col.moduleKey, false)})</span>
        ) : null}
      </span>
      <div className='ml-auto flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100'>
        <button
          type='button'
          className='flex h-8 w-8 items-center justify-center rounded text-red-600 hover:bg-red-50'
          aria-label='Remove column'
          onClick={(e) => {
            e.stopPropagation()
            onRemove(col.valueKey)
          }}
        >
          <FontAwesomeIcon icon={faTrash} className='text-sm' />
        </button>
      </div>
    </div>
  )
}

export function EditColumnsDrawer({
  open,
  mode = 'columns',
  moduleTitle,
  columns,
  onClose,
  filterOptions,
  filterOptionsLoading,
  addColumnsCatalog,
  addColumnsTabLabels,
  columnLayout,
  filteringConfig,
  sortingConfig,
  filterOptionsConfig
}: EditColumnsDrawerProps) {
  const normalizedRows = useMemo<ViewConfigColumn[]>(() => {
    return columns.filter((c): c is ViewConfigColumn => Boolean(c?.columnTitle && c?.valueKey))
  }, [columns])

  /**
   * Filter / sort UIs need the full catalog of columns the report supports
   * (visible + hidden), not just the currently-displayed set. Falls back to
   * normalizedRows when the catalog isn't provided (legacy callers).
   */
  const allColumns = useMemo<ViewConfigColumn[]>(() => {
    if (!addColumnsCatalog) return normalizedRows
    const seen = new Set<string>()
    const out: ViewConfigColumn[] = []
    for (const c of normalizedRows) {
      if (seen.has(c.valueKey)) continue
      seen.add(c.valueKey)
      out.push(c)
    }
    for (const c of [...addColumnsCatalog.deal, ...addColumnsCatalog.related]) {
      if (seen.has(c.valueKey)) continue
      seen.add(c.valueKey)
      out.push(c)
    }
    return out
  }, [normalizedRows, addColumnsCatalog])

  const [orderedColumns, setOrderedColumns] = useState<ViewConfigColumn[]>([])
  /** Last saved column keys (Apply / Add flow / open) — edit view is dirty when this differs from `orderedColumns`. */
  const [baselineColumnKeys, setBaselineColumnKeys] = useState<string[]>([])
  const [showAddColumns, setShowAddColumns] = useState(false)
  const [addPanelCheckedKeys, setAddPanelCheckedKeys] = useState<Set<string>>(() => new Set())
  const [draftFilters, setDraftFilters] = useState<FilterDraftRow[]>([])
  const [baselineFilters, setBaselineFilters] = useState<FilterValue[]>([])
  const [draftSorting, setDraftSorting] = useState<SortDraftRow[]>([])
  const [baselineSorting, setBaselineSorting] = useState<SortConfig[]>([])
  const orderedRef = useRef<ViewConfigColumn[]>([])

  useEffect(() => {
    orderedRef.current = orderedColumns
  }, [orderedColumns])

  useEffect(() => {
    if (open && mode === 'columns') {
      setOrderedColumns(normalizedRows)
      setBaselineColumnKeys(normalizedRows.map((c) => c.valueKey))
      setShowAddColumns(false)
    }
  }, [open, mode, normalizedRows])

  useEffect(() => {
    if (open && mode === 'sorting') {
      const current = sortingConfig?.currentSorting ?? []
      setBaselineSorting(current)
      setDraftSorting(
        current.length > 0
          ? current.map((row) => createSortDraftRow({ valueKey: row.key, direction: row.direction }))
          : [createSortDraftRow()]
      )
    }
  }, [open, mode, sortingConfig?.currentSorting])

  const orderedKeys = useMemo(() => orderedColumns.map((c) => c.valueKey), [orderedColumns])

  const isColumnLayoutDirty = useMemo(() => {
    if (!columnLayout) return false
    if (orderedKeys.length !== baselineColumnKeys.length) return true
    return orderedKeys.some((k, i) => k !== baselineColumnKeys[i])
  }, [columnLayout, orderedKeys, baselineColumnKeys])

  const isBaselineAlreadyReportingDefault = useMemo(() => {
    const def = columnLayout?.defaultTableColumnKeys
    if (!def?.length) return false
    if (def.length !== baselineColumnKeys.length) return false
    return def.every((k, i) => k === baselineColumnKeys[i])
  }, [columnLayout?.defaultTableColumnKeys, baselineColumnKeys])

  const toSortConfigs = useCallback((rows: SortDraftRow[]): SortConfig[] => {
    return rows
      .filter((row): row is SortDraftRow & { valueKey: string } => Boolean(row.valueKey))
      .map((row) => ({ key: row.valueKey, direction: row.direction }))
  }, [])

  const toFilterDraftRows = useCallback(
    (filters: FilterValue[], cols: ViewConfigColumn[]): FilterDraftRow[] => {
      const rows = filters
        .map((filter) => {
          // Matches the inverse of the filter-key formula used by the AntD column
          // (`filterKey || valueKey`) so that filters applied via the
          // header dropdown round-trip back into the drawer.
          const col = cols.find(
            (c) => (c.filterKey || c.valueKey) === filter.key
          )
          if (!col) return null
          const row: FilterDraftRow = createFilterDraftRow({
            valueKey: col.valueKey,
            operator: 'contains',
            value: ''
          })

          if (isRangeFilterColumn(col)) {
            const range = filter.value
            if (typeof range === 'object' && range !== null && !Array.isArray(range)) {
              const min =
                typeof (range as Record<string, unknown>).min === 'number'
                  ? String((range as Record<string, unknown>).min)
                  : ''
              const max =
                typeof (range as Record<string, unknown>).max === 'number'
                  ? String((range as Record<string, unknown>).max)
                  : ''
              return { ...row, operator: 'between', value: `${min}|${max}` }
            }
            return row
          }

          if (isDateRangeFilterColumn(col)) {
            const range = filter.value
            if (typeof range === 'object' && range !== null && !Array.isArray(range)) {
              const min = (range as Record<string, unknown>).min
              const max = (range as Record<string, unknown>).max
              const minStr = typeof min === 'string' ? min : ''
              const maxStr = typeof max === 'string' ? max : ''
              return { ...row, operator: 'between', value: `${minStr}|${maxStr}` }
            }
            return row
          }

          if ((isSelectFilterColumn(col) || isContactFilterColumn(col)) && !isCustomFilterColumn(col)) {
            return {
              ...row,
              operator: 'any_of',
              value: Array.isArray(filter.value) ? JSON.stringify(filter.value) : ''
            }
          }

          if (isCustomFilterColumn(col)) {
            return {
              ...row,
              operator: 'custom',
              value:
                typeof filter.value === 'object'
                  ? JSON.stringify(filter.value)
                  : Array.isArray(filter.value)
                    ? JSON.stringify(filter.value)
                    : String(filter.value ?? '')
            }
          }

          return { ...row, operator: 'contains', value: String(filter.value ?? '') }
        })
        .filter((row): row is FilterDraftRow => row !== null)
      return rows.length > 0 ? rows : [createFilterDraftRow()]
    },
    []
  )

  const toFilterValues = useCallback(
    (rows: FilterDraftRow[], cols: ViewConfigColumn[]): FilterValue[] => {
      return rows
        .filter((row) => Boolean(row.valueKey))
        .map((row) => {
          const col = cols.find((c) => c.valueKey === row.valueKey)
          if (!col) return null
          const key = col.filterKey || col.valueKey

          if (isRangeFilterColumn(col)) {
            const [minStr, maxStr] = (row.value ?? '').split('|')
            const range: { min?: number; max?: number } = {}
            const min = minStr === '' ? undefined : Number(minStr)
            const max = maxStr === '' ? undefined : Number(maxStr)
            if (min !== undefined && !Number.isNaN(min)) range.min = min
            if (max !== undefined && !Number.isNaN(max)) range.max = max
            if (range.min === undefined && range.max === undefined) return null
            return { key, value: range }
          }

          if (isDateRangeFilterColumn(col)) {
            const [minStr, maxStr] = (row.value ?? '').split('|')
            const range: { min?: string; max?: string } = {}
            if (minStr) range.min = minStr
            if (maxStr) range.max = maxStr
            if (range.min === undefined && range.max === undefined) return null
            return { key, value: range }
          }

          if ((isSelectFilterColumn(col) || isContactFilterColumn(col)) && !isCustomFilterColumn(col)) {
            if (!row.value?.trim()) return null
            try {
              const parsed = JSON.parse(row.value) as unknown
              if (!Array.isArray(parsed) || parsed.length === 0) return null
              return { key, value: parsed as string[] }
            } catch {
              return null
            }
          }

          if (isCustomFilterColumn(col)) {
            if (!row.value?.trim()) return null
            try {
              return { key, value: JSON.parse(row.value) as FilterValue['value'] }
            } catch {
              return null
            }
          }

          if (!row.value?.trim()) return null
          return { key, value: row.value }
        })
        .filter((row): row is FilterValue => row !== null)
    },
    []
  )

  const currentDraftFilters = useMemo(
    () => toFilterValues(draftFilters, allColumns),
    [draftFilters, allColumns, toFilterValues]
  )

  useEffect(() => {
    if (open && mode === 'filters') {
      const current = filteringConfig?.currentFilters ?? []
      setBaselineFilters(current)
      setDraftFilters(toFilterDraftRows(current, allColumns))
    }
  }, [open, mode, filteringConfig?.currentFilters, allColumns, toFilterDraftRows])

  const currentDraftSorting = useMemo(() => toSortConfigs(draftSorting), [draftSorting, toSortConfigs])

  const isSortingDirty = useMemo(() => {
    if (!sortingConfig) return false
    if (currentDraftSorting.length !== baselineSorting.length) return true
    return currentDraftSorting.some(
      (row, idx) => row.key !== baselineSorting[idx]?.key || row.direction !== baselineSorting[idx]?.direction
    )
  }, [sortingConfig, currentDraftSorting, baselineSorting])

  const isFilteringDirty = useMemo(() => {
    if (!filteringConfig) return false
    return stableSerialize(currentDraftFilters) !== stableSerialize(baselineFilters)
  }, [filteringConfig, currentDraftFilters, baselineFilters])

  const isFilteringAlreadyDefault = useMemo(() => {
    const defaults = filteringConfig?.defaultFilters ?? []
    return stableSerialize(defaults) === stableSerialize(baselineFilters)
  }, [filteringConfig?.defaultFilters, baselineFilters])

  const isSortingAlreadyDefault = useMemo(() => {
    const defaults = sortingConfig?.defaultSorting ?? []
    if (defaults.length !== baselineSorting.length) return false
    return defaults.every((row, idx) => {
      const current = baselineSorting[idx]
      return current?.key === row.key && current?.direction === row.direction
    })
  }, [sortingConfig?.defaultSorting, baselineSorting])

  useEffect(() => {
    if (showAddColumns) {
      setAddPanelCheckedKeys(new Set(orderedRef.current.map((c) => c.valueKey)))
    }
  }, [showAddColumns])

  /** Merge Add-columns checkboxes into local `orderedColumns` only; persist happens from Edit view via Apply. */
  const exitAddColumnsPanelToEditView = useCallback(() => {
    if (!columnLayout) {
      setShowAddColumns(false)
      return
    }
    if (!addColumnsCatalog) {
      setShowAddColumns(false)
      return
    }
    const keys = Array.from(addPanelCheckedKeys)
    const prevOrder = orderedRef.current.map((c) => c.valueKey)
    const kept = prevOrder.filter((k) => keys.includes(k))
    const added = keys.filter((k) => !prevOrder.includes(k))
    const addedOrdered = columnLayout.catalogFieldNameOrder.filter((k) => added.includes(k))
    const newOrderKeys = [...kept, ...addedOrdered]
    const newCols = newOrderKeys
      .map((k) => columnLayout.resolveColumn(k))
      .filter((c): c is ViewConfigColumn => c !== undefined)
    setOrderedColumns(newCols)
    setShowAddColumns(false)
  }, [addPanelCheckedKeys, addColumnsCatalog, columnLayout])

  const handleApplyColumnLayout = useCallback(() => {
    if (!columnLayout || !isColumnLayoutDirty) return
    const keys = orderedRef.current.map((c) => c.valueKey)
    void Promise.resolve(columnLayout.onPersistFieldOrder(keys)).then(() => {
      setBaselineColumnKeys(keys)
      onClose()
    })
  }, [columnLayout, isColumnLayoutDirty, onClose])

  const handleDrawerClose = useCallback(() => {
    if (mode === 'columns' && showAddColumns) {
      exitAddColumnsPanelToEditView()
      return
    }
    // Discard pending reorder/remove when closing without Apply (props stay server state).
    if (columnLayout && isColumnLayoutDirty) {
      setOrderedColumns(normalizedRows)
      setBaselineColumnKeys(normalizedRows.map((c) => c.valueKey))
    }
    if (mode === 'sorting' && isSortingDirty) {
      setDraftSorting(
        baselineSorting.length > 0
          ? baselineSorting.map((row) => createSortDraftRow({ valueKey: row.key, direction: row.direction }))
          : [createSortDraftRow()]
      )
    }
    if (mode === 'filters' && isFilteringDirty) {
      setDraftFilters(toFilterDraftRows(baselineFilters, allColumns))
    }
    onClose()
  }, [
    mode,
    showAddColumns,
    exitAddColumnsPanelToEditView,
    onClose,
    columnLayout,
    isColumnLayoutDirty,
    normalizedRows,
    allColumns,
    isFilteringDirty,
    baselineFilters,
    toFilterDraftRows,
    isSortingDirty,
    baselineSorting
  ])

  const drawerTitle = useMemo(() => {
    if (mode === 'filters') return `Filtering: ${moduleTitle}`
    if (mode === 'sorting') return `Sorting: ${moduleTitle}`
    if (showAddColumns) return 'Add columns'
    return `Edit columns: ${moduleTitle}`
  }, [mode, moduleTitle, showAddColumns])

  const closeTooltip = mode === 'columns' && showAddColumns ? 'Back' : 'Close'

  const moveColumn = useCallback((dragIndex: number, hoverIndex: number) => {
    setOrderedColumns((prev) => {
      const next = [...prev]
      const [removed] = next.splice(dragIndex, 1)
      next.splice(hoverIndex, 0, removed)
      return next
    })
  }, [])

  const removeColumn = useCallback((valueKey: string) => {
    setOrderedColumns((prev) => prev.filter((c) => c.valueKey !== valueKey))
  }, [])

  const handleResetToDefaults = useCallback(() => {
    if (!columnLayout) return
    void Promise.resolve(columnLayout.onResetToDefaults()).then(() => {
      onClose()
    })
  }, [columnLayout, onClose])

  const handleCancelColumnLayout = useCallback(() => {
    if (!columnLayout) return
    setOrderedColumns(normalizedRows)
    setBaselineColumnKeys(normalizedRows.map((c) => c.valueKey))
  }, [columnLayout, normalizedRows])

  const handleApplySorting = useCallback(() => {
    if (!sortingConfig || !isSortingDirty) return
    void Promise.resolve(sortingConfig.onApplySorting(currentDraftSorting)).then(() => {
      setBaselineSorting(currentDraftSorting)
      onClose()
    })
  }, [sortingConfig, isSortingDirty, currentDraftSorting, onClose])

  const handleApplyFilters = useCallback(() => {
    if (!filteringConfig || !isFilteringDirty) return
    void Promise.resolve(filteringConfig.onApplyFilters(currentDraftFilters)).then(() => {
      setBaselineFilters(currentDraftFilters)
      onClose()
    })
  }, [filteringConfig, isFilteringDirty, currentDraftFilters, onClose])

  const handleCancelFilters = useCallback(() => {
    if (!filteringConfig) return
    setDraftFilters(toFilterDraftRows(baselineFilters, normalizedRows))
  }, [filteringConfig, baselineFilters, normalizedRows, toFilterDraftRows])

  const handleResetFilters = useCallback(() => {
    if (!filteringConfig) return
    void Promise.resolve(filteringConfig.onResetToDefaults()).then(() => {
      onClose()
    })
  }, [filteringConfig, onClose])

  const handleClearAllFilters = useCallback(() => {
    if (!filteringConfig) return
    void Promise.resolve(filteringConfig.onApplyFilters([])).then(() => {
      onClose()
    })
  }, [filteringConfig, onClose])

  const handleCancelSorting = useCallback(() => {
    if (!sortingConfig) return
    setDraftSorting(
      baselineSorting.length > 0
        ? baselineSorting.map((row) => createSortDraftRow({ valueKey: row.key, direction: row.direction }))
        : [createSortDraftRow()]
    )
  }, [sortingConfig, baselineSorting])

  const handleResetSorting = useCallback(() => {
    if (!sortingConfig) return
    void Promise.resolve(sortingConfig.onResetToDefaults()).then(() => {
      onClose()
    })
  }, [sortingConfig, onClose])

  const handleClearAllSorting = useCallback(() => {
    if (!sortingConfig) return
    void Promise.resolve(sortingConfig.onApplySorting([])).then(() => {
      onClose()
    })
  }, [sortingConfig, onClose])

  return (
    <Drawer
      title={
        <div className='flex w-full min-w-0 items-center justify-between gap-2'>
          <span className='min-w-0 flex-1 truncate text-base font-semibold leading-snug text-white'>{drawerTitle}</span>
          <Tooltip title={closeTooltip}>
            <AntButton
              type='default'
              size='small'
              icon={<FontAwesomeIcon icon={mode === 'columns' && showAddColumns ? faArrowLeft : faTimes} />}
              onClick={handleDrawerClose}
              aria-label={mode === 'columns' && showAddColumns ? 'Back' : 'Close'}
              className='flex-shrink-0 bg-white/20 !border-white/40 !text-white hover:!bg-white/30 hover:!border-white/60 hover:!text-white'
              style={{ width: 32, height: 32, padding: 0, minWidth: 0 }}
            />
          </Tooltip>
        </div>
      }
      placement='right'
      width={400}
      open={open}
      onClose={handleDrawerClose}
      closable={false}
      classNames={{
        header:
          'shadow-lg !border-b-0 !h-10 !min-h-10 !max-h-10 !p-0 !px-4 flex items-center [&_.ant-drawer-header-title]:!m-0 [&_.ant-drawer-header-title]:!w-full [&_.ant-drawer-header-title]:!flex-1 [&_.ant-drawer-header-title]:!flex [&_.ant-drawer-header-title]:min-w-0 [&_.ant-drawer-header-title]:items-center'
      }}
      styles={{
        header: {
          background: portalGradients.navbar,
          height: 40,
          minHeight: 40,
          maxHeight: 40,
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          borderBottom: 'none',
          boxSizing: 'border-box'
        },
        content: {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        },
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          padding: '8px 16px 16px'
        }
      }}
    >
      <div className='flex min-h-0 w-full flex-1 flex-col'>
        {mode === 'filters' && (() => {
          const optsDirty = Boolean(filterOptionsConfig?.isDirty)
          const anyDirty = (filteringConfig && isFilteringDirty) || optsDirty
          const optsAlreadyDefault = filterOptionsConfig?.isAlreadyDefault ?? true
          const allAlreadyDefault = isFilteringAlreadyDefault && optsAlreadyDefault

          const handleUnifiedApply = async () => {
            if (filteringConfig && isFilteringDirty) await Promise.resolve(handleApplyFilters())
            if (filterOptionsConfig && optsDirty) await Promise.resolve(filterOptionsConfig.onApply())
            // Always close on Apply, even when only Options changed (the
            // existing handleApplyFilters closes via onClose only when it
            // actually fires; cover the options-only case here).
            onClose()
          }
          const handleUnifiedCancel = () => {
            if (filteringConfig && isFilteringDirty) handleCancelFilters()
            if (filterOptionsConfig && optsDirty) filterOptionsConfig.onCancel()
          }
          const handleUnifiedReset = async () => {
            if (filteringConfig && !isFilteringAlreadyDefault) await Promise.resolve(handleResetFilters())
            if (filterOptionsConfig && !optsAlreadyDefault) await Promise.resolve(filterOptionsConfig.onResetToDefaults())
          }

          return (
          <div className='flex min-h-0 flex-1 flex-col gap-0'>
            <div className='flex shrink-0 flex-wrap items-center justify-between gap-2'>
              <Button
                type='text'
                variant='default'
                size='small'
                disabled={!filteringConfig || baselineFilters.length === 0}
                className={
                  filteringConfig && baselineFilters.length > 0
                    ? '!text-amber-600 hover:!text-amber-700'
                    : '!text-gray-400'
                }
                icon={<FontAwesomeIcon icon={faTimes} className='text-xs' />}
                onClick={handleClearAllFilters}
              >
                Clear all
              </Button>
              <div className='flex items-center gap-1.5'>
                {anyDirty ? (
                  <>
                    <Button variant='cancel' size='small' onClick={handleUnifiedCancel}>
                      Cancel
                    </Button>
                    <Button
                      variant='save'
                      size='small'
                      icon={<FontAwesomeIcon icon={faCheck} className='text-xs' />}
                      onClick={handleUnifiedApply}
                    >
                      Apply
                    </Button>
                  </>
                ) : (
                  <Button
                    type='text'
                    variant='default'
                    size='small'
                    disabled={allAlreadyDefault}
                    className={!allAlreadyDefault ? '' : '!text-gray-400'}
                    icon={<FontAwesomeIcon icon={faArrowRotateLeft} className='text-xs' />}
                    onClick={handleUnifiedReset}
                  >
                    Reset to default
                  </Button>
                )}
              </div>
            </div>
            <Divider className='!mt-1 !mb-2 shrink-0' />
            {filterOptionsConfig ? (
              <>
                <div className='shrink-0'>{filterOptionsConfig.panel}</div>
                <Divider className='!mt-2 !mb-2 shrink-0' />
              </>
            ) : null}
            <div className='shrink-0 mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500'>
              Filters
            </div>
            <FilterPanel
              columns={allColumns}
              filterOptions={filterOptions}
              filterOptionsLoading={filterOptionsLoading}
              value={draftFilters}
              onChange={setDraftFilters}
            />
          </div>
          )
        })()}
        {mode === 'sorting' && (
          <div className='flex min-h-0 flex-1 flex-col gap-0'>
            <div className='flex shrink-0 flex-wrap items-center justify-between gap-2'>
              <Button
                type='text'
                variant='default'
                size='small'
                disabled={!sortingConfig || baselineSorting.length === 0}
                className={
                  sortingConfig && baselineSorting.length > 0
                    ? '!text-amber-600 hover:!text-amber-700'
                    : '!text-gray-400'
                }
                icon={<FontAwesomeIcon icon={faTimes} className='text-xs' />}
                onClick={handleClearAllSorting}
              >
                Clear all
              </Button>
              <div className='flex items-center gap-1.5'>
                {sortingConfig && isSortingDirty ? (
                  <>
                    <Button variant='cancel' size='small' onClick={handleCancelSorting}>
                      Cancel
                    </Button>
                    <Button
                      variant='save'
                      size='small'
                      icon={<FontAwesomeIcon icon={faCheck} className='text-xs' />}
                      onClick={handleApplySorting}
                    >
                      Apply
                    </Button>
                  </>
                ) : (
                  <Button
                    type='text'
                    variant='default'
                    size='small'
                    disabled={!sortingConfig || isSortingAlreadyDefault}
                    className={sortingConfig && !isSortingAlreadyDefault ? '' : '!text-gray-400'}
                    icon={<FontAwesomeIcon icon={faArrowRotateLeft} className='text-xs' />}
                    onClick={handleResetSorting}
                  >
                    Reset to default
                  </Button>
                )}
              </div>
            </div>
            <Divider className='!mt-1 !mb-2 shrink-0' />
            <SortPanel columns={allColumns} value={draftSorting} onChange={setDraftSorting} />
          </div>
        )}
        {mode === 'columns' &&
          (showAddColumns ? (
            <AddColumnsPanel
              orderedColumns={orderedColumns}
              addColumnsCatalog={addColumnsCatalog}
              allColumns={normalizedRows}
              tabLabels={addColumnsTabLabels}
              {...(columnLayout
                ? { checkedKeys: addPanelCheckedKeys, onCheckedKeysChange: setAddPanelCheckedKeys }
                : {})}
            />
          ) : (
            <div className='flex min-h-0 flex-1 flex-col gap-0'>
              <div className='flex shrink-0 flex-wrap items-center justify-between gap-2'>
                <Button
                  type='link'
                  variant='default'
                  size='small'
                  className='!p-0 !h-auto text-[#1677ff] hover:!text-[#4096ff]'
                  icon={<FontAwesomeIcon icon={faPlus} className='text-xs' />}
                  onClick={() => setShowAddColumns(true)}
                >
                  Add columns
                </Button>
                <div className='flex flex-wrap items-center gap-1.5'>
                  {columnLayout && isColumnLayoutDirty ? (
                    <>
                      <Button variant='cancel' size='small' onClick={handleCancelColumnLayout}>
                        Cancel
                      </Button>
                      <Button
                        variant='save'
                        size='small'
                        icon={<FontAwesomeIcon icon={faCheck} className='text-xs' />}
                        onClick={handleApplyColumnLayout}
                      >
                        Apply
                      </Button>
                    </>
                  ) : (
                    <Button
                      type='text'
                      variant='default'
                      size='small'
                      disabled={!columnLayout || isBaselineAlreadyReportingDefault}
                      className={
                        columnLayout && !isBaselineAlreadyReportingDefault ? '' : '!text-gray-400'
                      }
                      icon={<FontAwesomeIcon icon={faArrowRotateLeft} className='text-xs' />}
                      onClick={handleResetToDefaults}
                    >
                      Reset to default
                    </Button>
                  )}
                </div>
              </div>
              <Divider className='!mt-1 !mb-2 shrink-0' />
              <DndProvider backend={HTML5Backend}>
                <div className='flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1'>
                  {orderedColumns.map((col, index) => (
                    <DraggableColumnRow
                      key={col.valueKey}
                      col={col}
                      index={index}
                      moveColumn={moveColumn}
                      onRemove={removeColumn}
                    />
                  ))}
                </div>
              </DndProvider>
            </div>
          ))}
      </div>
    </Drawer>
  )
}
